import {
  IxdPreviewRenderer,
  IxdPreviewRendererRef,
} from "@/components/ixd-preview";
import { IxdChunk } from "@/apis/compare/route";
import type { FsType } from "@/apis/ixd-editor/types";
import type {
  CellChangeOrigin,
  CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import { useIxdDsdCompareStore } from "@/stores/ixd-dsd-compare";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";

interface IxdPreviewPanelProps {
  ixdRendererRef: React.RefObject<IxdPreviewRendererRef>;
  activeChunkId: string | null;
  /** 매핑된 IXD chunks (부모에서 전달) */
  currentIxdChunks: IxdChunk[];
  /** 현재 선택된 IXD chunk (부모에서 전달) */
  activeIxdChunk: IxdChunk | null;

  // 편집 관련 (모두 부모가 useIxdEditSession에서 끌어와 주입)
  /** 편집 활성 (capability && intent). renderer의 editable prop으로 전달 */
  editable: boolean;
  cellChanges: Record<string, string>;
  changeOrigins?: Record<string, CellChangeOrigin>;
  cellIdentityByNormalizedCellId: Map<string, string>;
  identityChanges: Map<string, string>;
  identityLocationsByKey: Map<string, CellIdentityLocation[]>;
  chunkFsTypeById: Map<string, FsType>;
  onCellEdit: (
    fsType: FsType,
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
  onCellEditStart?: () => void;
  onCellEditBatch: (
    entries: Array<{
      fsType: FsType;
      cellId: string;
      newValue: string;
      identityKey?: string;
      locationUid?: string;
    }>,
  ) => number[] | void;
  showRawValueInEdit: boolean;
  onUndo: () => void;
  onFocusIdentityLocation?: (location: CellIdentityLocation) => boolean | void;

  /** 선택된 IXD cellId 전파 — IXD 선택 → DSD 강조에 사용 */
  onSelectedCellIdsChange?: (
    cellIds: string[],
    identityKeys?: Array<string | null>,
    dlViewIds?: Array<string | null>,
  ) => void;
  /** DSD 선택에 매칭되는 IXD cellId — counterpart 외곽선 */
  counterpartCellIds?: string[];
  counterpartIdentityKeys?: string[];
  /** 전체 매칭된 IXD 원본 cellId 목록 — 매칭 DSD 없는 셀 빨간 강조 계산용 */
  matchedCellIds?: string[];
  /** 매칭 DSD 가 없는 IXD 값 셀을 상시 빨간색으로 강조 */
  showUnmatchedStatus?: boolean;
  /** IXD 셀 직접 클릭 시 외부의 프로그래매틱 포커스 강조를 해제하는 콜백 */
  onClearFocus?: () => void;
  /**
   * 연동 판정 데이터(editorPreview) 가 아직 도착 전인지. true 면 dog-ear 가
   * 아직 계산되지 않았음을 칩으로 알려, "연동 셀이 원래 없는 것"처럼 보이는
   * silent 누락을 막는다.
   */
  isLinkageLoading?: boolean;
}

const IxdPreviewPanel = ({
  ixdRendererRef,
  activeChunkId,
  currentIxdChunks,
  activeIxdChunk,
  editable,
  cellChanges,
  changeOrigins,
  cellIdentityByNormalizedCellId,
  identityChanges,
  identityLocationsByKey,
  chunkFsTypeById,
  onCellEdit,
  onCellEditStart,
  onCellEditBatch,
  showRawValueInEdit,
  onUndo,
  onFocusIdentityLocation,
  onSelectedCellIdsChange,
  counterpartCellIds,
  counterpartIdentityKeys,
  matchedCellIds,
  showUnmatchedStatus,
  onClearFocus,
  isLinkageLoading,
}: IxdPreviewPanelProps) => {
  const setActiveIxdChunkId = useIxdDsdCompareStore(
    (s) => s.setActiveIxdChunkId,
  );

  const setActiveIxdChunk = useCallback(
    (chunk: IxdChunk) => setActiveIxdChunkId(chunk.id),
    [setActiveIxdChunkId],
  );

  // DSD 대목차를 바꾸면 IXD 스크롤을 맨 위로 되돌린다. 렌더러는 chunk 목록이 바뀌어도
  // 재마운트되지 않아(캐러셀 제거로 key 가 사라졌다) scrollTop 이 그대로 남고, 새 대목차의
  // IXD 가 문서 중간부터 보인다. 포커스 스크롤은 이 뒤(rAF/재시도 루프)에 오므로 안 밀린다.
  useEffect(() => {
    ixdRendererRef.current?.getScrollContainer()?.scrollTo({ top: 0 });
  }, [activeChunkId, ixdRendererRef]);

  /**
   * 현재 활성 DSD chunk에 매핑된 IXD chunk 목록.
   * chunkDetail에서 직접 가져옴.
   */
  const activeMappedIxdChunks = useMemo<IxdChunk[]>(() => {
    if (currentIxdChunks.length > 0) return currentIxdChunks;
    return activeIxdChunk ? [activeIxdChunk] : [];
  }, [currentIxdChunks, activeIxdChunk]);

  /**
   * 이 패널이 그리는 chunk 안의 위치면 렌더러가 직접 포커싱하고, 밖이면 부모에 위임한다
   * (다른 DSD 대목차로 이동해야 하는 경우).
   */
  const focusIdentityLocation = useCallback(
    (location: CellIdentityLocation): boolean => {
      const targetChunk = activeMappedIxdChunks.find(
        (chunk) => chunk.id === location.chunkId,
      );
      if (!targetChunk) {
        return onFocusIdentityLocation
          ? onFocusIdentityLocation(location) !== false
          : false;
      }
      // 포커싱에 성공했을 때만 활성 chunk 를 옮긴다. 먼저 옮기면 실패했을 때 화면은
      // 그대로인데 섹션 탭만 다른 chunk 로 가 있어 사용자에게 아무 피드백이 없다.
      const focused =
        ixdRendererRef.current?.focusCellLocation(location) ?? false;
      if (focused) setActiveIxdChunk(targetChunk);
      return focused;
    },
    [
      activeMappedIxdChunks,
      ixdRendererRef,
      onFocusIdentityLocation,
      setActiveIxdChunk,
    ],
  );

  return (
    <div className="box-border flex flex-1 min-w-0 min-h-0 relative">
      {isLinkageLoading && activeMappedIxdChunks.length > 0 && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full border border-line-normal bg-background-base px-3 py-1.5 shadow-lg">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-base" />
          <span className="label-2-normal-medium text-label-alternative">
            연동 정보 불러오는 중…
          </span>
        </div>
      )}
      {activeMappedIxdChunks.length > 0 ? (
        <div className="absolute inset-0 flex flex-col rounded-[0.75rem] border border-line-normal border-solid bg-white overflow-hidden shadow-lg">
          <div className="flex-1 min-h-0 min-w-0">
            <IxdPreviewRenderer
              ref={ixdRendererRef}
              chunks={activeMappedIxdChunks}
              selectedChunkId={activeIxdChunk?.id}
              editable={editable}
              cellChanges={cellChanges}
              changeOrigins={changeOrigins}
              cellIdentityByNormalizedCellId={cellIdentityByNormalizedCellId}
              identityChanges={identityChanges}
              identityLocationsByKey={identityLocationsByKey}
              chunkFsTypeById={chunkFsTypeById}
              onCellEdit={onCellEdit}
              onCellEditStart={onCellEditStart}
              onCellEditBatch={onCellEditBatch}
              onFocusIdentityLocation={focusIdentityLocation}
              showRawValueInEdit={showRawValueInEdit}
              onUndo={onUndo}
              onSelectedCellIdsChange={onSelectedCellIdsChange}
              counterpartCellIds={counterpartCellIds}
              counterpartIdentityKeys={counterpartIdentityKeys}
              matchedCellIds={matchedCellIds}
              showUnmatchedStatus={showUnmatchedStatus}
              onClearFocus={onClearFocus}
            />
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-[0.75rem] border border-line-normal border-solid bg-white shadow-2xl text-neutral-400">
          <span className="text-sm">
            {activeChunkId
              ? "매칭된 IXD 대목차를 찾을 수 없습니다"
              : "DSD 대목차를 선택하면 매칭된 IXD preview가 표시됩니다"}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(IxdPreviewPanel);
